import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import { 
  Typography, 
  Grid, 
  Divider, 
  IconButton, 
  Fab, 
  Popover, 
  Chip,
  Card,
  CardContent,
  Box,
  useTheme,
  useMediaQuery,
  alpha
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import DetailsIcon from '@mui/icons-material/Details';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import AddIcon from '@mui/icons-material/Add';
import Lightbox from "react-awesome-lightbox";
import "react-lazy-load-image-component/src/effects/blur.css";

import { useSelector, useDispatch } from "react-redux";
import { addDish } from "store/actions/dish";
import { updateDishPosition } from "store/actions/order";

// Modern styling using sx prop - function to access theme
const getStyles = (theme) => ({
  card: {
    margin: { xs: 0.5, sm: 1 },
    borderRadius: 3,
    background: '#ffffff',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    width: '100%',
    maxWidth: '100%',
    overflowX: 'hidden',
  },
  cardContent: {
    padding: { xs: 2, sm: 3 },
    '&:last-child': {
      paddingBottom: { xs: 2, sm: 3 },
    },
    width: '100%',
    maxWidth: '100%',
    overflowX: 'hidden',
  },
  imageContainer: {
    position: 'relative',
    borderRadius: 2,
    overflow: 'hidden',
    marginBottom: 2,
    cursor: 'pointer',
  },
  dishImage: {
    width: '100%',
    height: { xs: 200, sm: 250 },
    objectFit: 'cover',
    borderRadius: 2,
  },
  dishImageSmall: {
    width: 138,
    height: 138,
    objectFit: 'cover',
    borderRadius: 6,
    borderWidth: 0,
    flexShrink: 0,
  },
  title: {
    fontSize: { xs: '1.1rem', sm: '1.25rem' },
    fontWeight: 700,
    lineHeight: 1.3,
    marginBottom: 0.5,
    color: 'text.primary',
  },
  altTitle: {
    fontSize: { xs: '0.9rem', sm: '1rem' },
    fontWeight: 500,
    color: 'text.secondary',
    marginBottom: 1,
    fontStyle: 'italic',
  },
  priceContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 1,
  },
  price: {
    fontSize: { xs: '1.25rem', sm: '1.5rem' },
    fontWeight: 800,
    color: 'primary.main',
  },
  actionButton: {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      background: theme.palette.primary.dark,
    },
  },
  infoButton: {
    color: 'primary.main',
    marginLeft: 1,
  },
  recommendedIcon: {
    color: '#fccc14',
    marginLeft: 1,
    fontSize: '1.2rem',
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 1,
    marginTop: 1,
  },
  tag: {
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: 2,
    padding: '4px 8px',
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: 'primary.main',
    border: '1px solid',
    borderColor: alpha(theme.palette.primary.main, 0.2),
  },
  spicyTag: {
    backgroundColor: alpha('#ff6b6b', 0.1),
    color: '#ff6b6b',
    borderColor: alpha('#ff6b6b', 0.2),
  },
  descriptionPopover: {
    maxWidth: 300,
    padding: 2,
  },
  divider: {
    margin: '8px 0',
    opacity: 0.3,
  },
});

const Dish = (props) => {
  const { item } = props;
  const history = useHistory();
  const theme = useTheme();
  const styles = getStyles(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [imageVisible, setImageVisible] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const restaurant = useSelector((state) => state.menu.restaurant);
  const menuTableId = useSelector((state) => state.menu.tableId);
  const dispatch = useDispatch();

  const showDish = (dish) => dispatch(addDish(dish));
  const updatePosition = (position) => dispatch(updateDishPosition(position));
  // const setCategory = (idx) => dispatch(selectedCategory(idx));
  // const selectedCategoryIdx = useSelector(
  //   (state) => state.menu.selectedCategoryIdx
  // );

  // const [isRequiredOptionsChecked, setIsRequiredOptionsChecked] = useState(
  //   false
  // );

  const showDishDetail = () => {
    updatePosition(item.id);
    showDish({ ...item });

    // Save current scroll position before navigating
    try {
      sessionStorage.setItem('menuScrollPosition', window.pageYOffset.toString());
      sessionStorage.setItem('menuScrollRestoreTime', Date.now().toString());
    } catch (e) {
      // Safari private mode can throw errors on sessionStorage access
      console.warn('Failed to save scroll position:', e);
    }
    
    history.push("/dishDetail");
  };



  // a function to compare trimmed item name and trimmed altname
  const compareName = (name, altname) => {
    if (!name || !altname) {
      return false;
    }
    return name.trim().toLowerCase() === altname.trim().toLowerCase();
  };


  const priceString = () => {
    let priceStr = `${restaurant && restaurant.currencySymbol}${props.item.price}`;
    // if (props.item.sizes && props.item.sizes.length > 0) {
    //    const priceList = props.item.sizes.map(size => Number(size.price));
    //     const minimumPrice = Math.min(...priceList);
    //     priceStr = `From $${minimumPrice}`
    // }

    return priceStr;
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const tagText = (tag) => {
    if (tag === "Spicy") {
      return "🌶️";
    }
    if (tag === "Hotter") {
      return "🌶️🌶️";
    }
    if (tag === "Hottest") {
      return "🌶️🌶️🌶️";
    }

    return tag;
  };

  const isBigImage = () => {
    return restaurant.isBigMenuImageUsed === true;
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  
  return (
    <React.Fragment>
      <div id={item.id}></div>
      <Card sx={styles.card}>
          <CardContent sx={styles.cardContent}>
            <Grid container spacing={2} alignItems="flex-start">
              {item.image && (
                <Grid item xs="auto">
                  <Box 
                    onClick={() => setImageVisible(true)}
                  >
                    <Box
                      component="img"
                      alt={item.name}
                      src={item.image}
                      sx={styles.dishImageSmall}
                    />
                  </Box>
                </Grid>
              )}
              
              <Grid item xs>
                <Box>
                  <Typography sx={styles.title}>
                    {item.name}
                  </Typography>
                  
                  {!compareName(item.altName, item.name) && item.altName && (
                    <Typography sx={styles.altTitle}>
                      {item.altName}
                    </Typography>
                  )}
                  
                  <Box sx={styles.priceContainer}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={styles.price}>
                        {priceString()}
                      </Typography>
                      
                      {item.description && item.description.trim() !== "" && (
                        <IconButton 
                          aria-label="info" 
                          onClick={handleClick}
                          sx={styles.infoButton}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      )}
                      
                      {item.tags && item.tags.find((tag) => tag === "Recommended") && (
                        <ThumbUpIcon sx={styles.recommendedIcon} />
                      )}
                    </Box>
                    
                    <Fab 
                      sx={styles.actionButton}
                      size="small" 
                      onClick={showDishDetail}
                      aria-label={menuTableId === 'onlineMenu' ? "view details" : "add to cart"}
                    >
                      {menuTableId === 'onlineMenu' ? (
                        <DetailsIcon />
                      ) : (
                        <AddIcon />
                      )}
                    </Fab>
                  </Box>
                  
                  {item.tags && item.tags.filter((tag) => tag !== "Recommended").length > 0 && (
                    <Box sx={styles.tagsContainer}>
                      {item.tags
                        .filter((tag) => tag !== "Recommended")
                        .map((tag) => (
                          <Chip
                            key={tag}
                            label={tagText(tag)}
                            size="small"
                            sx={{
                              ...styles.tag,
                              ...(tag.includes('Spicy') || tag.includes('Hot') ? styles.spicyTag : {})
                            }}
                          />
                        ))}
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
          
          {item.description && item.description.trim() !== "" && (
            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}
            >
              <Typography sx={styles.descriptionPopover}>
                {item.description}
              </Typography>
            </Popover>
          )}
        </Card>
      
      {item.image && imageVisible && (
        <Box sx={{ position: 'fixed', zIndex: 9999 }}>
          <Lightbox
            image={[item.image]}
            allowZoom={false}
            allowRotate={false}
            onClose={() => setImageVisible(false)}
          />
        </Box>
      )}
      
      <Divider sx={styles.divider} />
    </React.Fragment>
  );
};

export default Dish;
